import React, { useEffect, useState, useCallback } from 'react';
import {
  BookOpen, Sparkles, Plus, Trash2, Star, MessageSquare,
  Download, Eye, FileText, Loader2, ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError, BACKEND_URL } from '../../lib/api';
import { focalPosition } from '../../lib/photoFocal';
import { toast } from 'sonner';

export function MemoryBookTab({ memorial, memorialId, onChange }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [completion, setCompletion] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPages, setPreviewPages] = useState(null);
  const [expandedPhoto, setExpandedPhoto] = useState(null);
  const [showAddMessage, setShowAddMessage] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get(`/memorials/${memorialId}/book/settings`),
        api.get(`/memorials/${memorialId}/media?visibility=public`),
      ]);
      setSettings(s.data.settings);
      setChapterOptions(s.data.chapter_options || []);
      setCompletion(s.data.completion || {});
      // Only photos that are not opted-out of the book.
      const all = (m.data.media || []).filter(
        (p) => p.kind === 'image' && p.book_include !== false,
      );
      setPhotos(all);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [memorialId]);

  useEffect(() => { load(); }, [load]);

  const patchSettings = async (patch) => {
    try {
      const { data } = await api.patch(`/memorials/${memorialId}/book/settings`, patch);
      setSettings(data);
      toast.success('Saved');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patchPhoto = async (mediaId, patch) => {
    // Optimistic update
    setPhotos((prev) => prev.map((p) => p.media_id === mediaId ? { ...p, ...patch } : p));
    try {
      await api.patch(`/media/${mediaId}`, patch);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const addMessage = async (name, relationship, message) => {
    try {
      await api.post(`/memorials/${memorialId}/book/family-messages`,
        { name, relationship, message });
      toast.success('Message added');
      setShowAddMessage(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteMessage = async (entryId) => {
    if (!window.confirm('Remove this message from the book?')) return;
    try {
      await api.delete(`/memorials/${memorialId}/book/family-messages/${entryId}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPreview = async () => {
    setShowPreview(true);
    setPreviewPages(null);
    try {
      const { data } = await api.get(`/memorials/${memorialId}/book/preview`);
      setPreviewPages(data);
    } catch (e) { toast.error(formatApiError(e)); setShowPreview(false); }
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      await api.post(`/memorials/${memorialId}/book/generate-pdf`);
      toast.success('Memory Book PDF generated');
      await load();
      // Open in new tab
      window.open(`${BACKEND_URL}/api/memorials/${memorialId}/book/pdf`, '_blank');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  if (loading || !settings) {
    return <div className="ml-card" data-testid="memory-book-loading">Loading Memory Book…</div>;
  }

  const heroCount = photos.filter((p) => p.book_hero).length;
  const captionedCount = photos.filter((p) => (p.book_caption || '').trim()).length;
  const hasGenerated = !!settings.last_generated_at;

  return (
    <div className="space-y-5" data-testid="memory-book-tab">
      {/* Header card — interactive book is the PRIMARY product. PDF is a quiet
          secondary download for printing / archival. */}
      <div className="ml-card">
        <div className="flex flex-col gap-5">
          <div className="min-w-0">
            <div className="ml-overline flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Memory Book
            </div>
            <h2 className="font-serif text-2xl mt-1 text-[hsl(var(--primary))]">
              A living, interactive ancient hardcover.
            </h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed max-w-2xl">
              Their story, bound in a leather memorial book families can hold, flip, and share.
              Write the story behind each photo, add a dedication and family messages, then
              open the interactive book to browse it as a real two-page spread that turns page
              by page. A printable PDF copy is also available for archival.
            </p>
          </div>

          {/* Primary action: Open the interactive Memory Book */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => navigate(`/m/${memorial?.slug}/book`)}
              disabled={photos.length === 0 || !memorial?.slug}
              className="ml-btn-gold inline-flex items-center gap-2 !py-3 !px-6 text-sm font-medium shadow-lg"
              data-testid="memory-book-open-interactive-btn"
              title="Read the interactive book"
            >
              <BookOpen className="w-5 h-5" />
              Open Memory Book
            </button>
            <button
              onClick={openPreview}
              disabled={photos.length === 0}
              className="inline-flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] underline-offset-4 hover:underline"
              data-testid="memory-book-preview-btn"
            >
              <Eye className="w-3.5 h-3.5" /> See outline
            </button>
            {photos.length === 0 && (
              <span className="text-xs italic text-[hsl(var(--muted-foreground))]">
                Add public photos in the Memories tab to unlock the book.
              </span>
            )}
          </div>

          {/* Secondary: PDF download for archival / print */}
          <div className="border-t border-[hsl(var(--border))] pt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
            <span className="text-[hsl(var(--muted-foreground))] uppercase tracking-widest text-[10px]">
              Also available
            </span>
            <button
              onClick={generatePDF}
              disabled={generating || photos.length === 0}
              className="inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] disabled:opacity-50"
              data-testid="memory-book-generate-btn"
              title="Generate a printable PDF copy"
            >
              {generating
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
                : <><FileText className="w-3.5 h-3.5" /> Generate printable PDF</>}
            </button>
            {hasGenerated && (
              <a
                href={`${BACKEND_URL}/api/memorials/${memorialId}/book/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
                data-testid="memory-book-download-latest"
              >
                <Download className="w-3.5 h-3.5" /> Download latest PDF
              </a>
            )}
          </div>
        </div>

        {/* Completion grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Stat label="Photos in book" value={photos.length} />
          <Stat label="Hero photos" value={heroCount} highlight={heroCount > 0} />
          <Stat label="With stories" value={`${captionedCount} / ${photos.length}`} />
          <Stat label="Family messages" value={(settings.family_messages || []).length} />
        </div>
      </div>

      {/* Dedication + quote */}
      <div className="ml-card" data-testid="memory-book-dedication-card">
        <div className="ml-overline">Dedication page</div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
          The first page after the cover. Add a short message from the family and an optional quote.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Family dedication</span>
            <textarea
              defaultValue={settings.dedication_message || ''}
              onBlur={(e) => { if (e.target.value !== settings.dedication_message) patchSettings({ dedication_message: e.target.value }); }}
              rows={3}
              maxLength={2000}
              placeholder="For Dad, who taught us patience and grace…"
              className="ml-input mt-1 w-full"
              data-testid="memory-book-dedication-input"
            />
          </label>
          <div className="grid sm:grid-cols-[1fr_auto] gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Optional quote / scripture</span>
              <input
                defaultValue={settings.dedication_quote || ''}
                onBlur={(e) => { if (e.target.value !== settings.dedication_quote) patchSettings({ dedication_quote: e.target.value }); }}
                maxLength={500}
                placeholder="To live in hearts we leave behind is not to die."
                className="ml-input mt-1 w-full"
                data-testid="memory-book-quote-input"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Attribution</span>
              <input
                defaultValue={settings.dedication_quote_attribution || ''}
                onBlur={(e) => { if (e.target.value !== settings.dedication_quote_attribution) patchSettings({ dedication_quote_attribution: e.target.value }); }}
                maxLength={200}
                placeholder="Thomas Campbell"
                className="ml-input mt-1 w-full"
                data-testid="memory-book-attribution-input"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Settings — chapters + sort + stats toggle */}
      <div className="ml-card" data-testid="memory-book-settings-card">
        <div className="ml-overline">Book structure</div>
        <div className="grid sm:grid-cols-3 gap-4 mt-3">
          <ToggleRow
            testid="settings-chapters-toggle"
            label="Use chapter sections"
            description="Group photos by Early Years, Family Life, Career, etc."
            checked={!!settings.chapters_enabled}
            onChange={(v) => patchSettings({ chapters_enabled: v })}
          />
          <div>
            <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Photo order</span>
            <select
              value={settings.sort_mode}
              onChange={(e) => patchSettings({ sort_mode: e.target.value })}
              className="ml-input mt-1 w-full"
              data-testid="settings-sort-mode"
            >
              <option value="chronological">Chronological (by date)</option>
              <option value="manual">Manual order</option>
            </select>
          </div>
          <ToggleRow
            testid="settings-stats-toggle"
            label="Include 'About this book' stats page"
            description="A small summary page near the end."
            checked={!!settings.show_stats_page}
            onChange={(v) => patchSettings({ show_stats_page: v })}
          />
        </div>
      </div>

      {/* Per-photo editor */}
      <div className="ml-card" data-testid="memory-book-photos-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="ml-overline">Photo Stories</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              For each public photo, add a title, date, location, and the story behind it. Mark a few as <strong>Hero</strong> to make them full-page features.
            </p>
          </div>
        </div>

        {photos.length === 0 ? (
          <div className="text-sm text-[hsl(var(--muted-foreground))] mt-4 italic">
            Upload public photos in the Memories tab to start writing stories.
          </div>
        ) : (
          <div className="mt-4 space-y-2" data-testid="memory-book-photo-list">
            {photos.map((p) => (
              <PhotoStoryRow
                key={p.media_id}
                photo={p}
                expanded={expandedPhoto === p.media_id}
                onToggleExpand={() => setExpandedPhoto(expandedPhoto === p.media_id ? null : p.media_id)}
                onPatch={(patch) => patchPhoto(p.media_id, patch)}
                chapterOptions={chapterOptions}
                chaptersEnabled={!!settings.chapters_enabled}
              />
            ))}
          </div>
        )}
      </div>

      {/* Family messages */}
      <div className="ml-card" data-testid="memory-book-messages-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="ml-overline flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Messages from family and friends</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              Tributes that appear in a dedicated section near the end of the book.
            </p>
          </div>
          <button
            onClick={() => setShowAddMessage(true)}
            className="ml-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
            data-testid="add-family-message-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add message
          </button>
        </div>
        <div className="mt-4 space-y-2">
          {(settings.family_messages || []).length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))] italic">No messages yet.</p>
          ) : (
            (settings.family_messages || []).map((m) => (
              <div key={m.id} className="border border-[hsl(var(--accent))]/30 rounded-lg p-3 bg-[hsl(var(--accent))]/5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[hsl(var(--primary))]">
                      {m.name}
                      {m.relationship && <span className="text-[hsl(var(--muted-foreground))] text-xs ml-2">· {m.relationship}</span>}
                    </div>
                    <p className="text-sm italic text-[hsl(var(--foreground))] mt-1 leading-relaxed">"{m.message}"</p>
                  </div>
                  <button
                    onClick={() => deleteMessage(m.id)}
                    className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] shrink-0"
                    data-testid={`delete-message-${m.id}`}
                    aria-label="Remove message"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showAddMessage && (
        <AddMessageModal
          onClose={() => setShowAddMessage(false)}
          onSubmit={addMessage}
        />
      )}

      {showPreview && (
        <PreviewModal
          onClose={() => setShowPreview(false)}
          data={previewPages}
        />
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`border rounded-lg px-3 py-3 ${highlight ? 'border-[hsl(var(--accent))]/60 bg-[hsl(var(--accent))]/10' : 'border-[hsl(var(--border))]'}`}>
      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className={`text-xl font-serif mt-1 ${highlight ? 'text-[hsl(var(--accent))]' : 'text-[hsl(var(--primary))]'}`}>{value}</div>
    </div>
  );
}

function ToggleRow({ testid, label, description, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testid}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <div className="text-sm text-[hsl(var(--primary))]">{label}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{description}</div>
      </div>
    </label>
  );
}

function PhotoStoryRow({ photo, expanded, onToggleExpand, onPatch, chapterOptions, chaptersEnabled }) {
  const thumbUrl = photo.external_url
    ? (photo.external_url.startsWith('http') ? photo.external_url : `${BACKEND_URL}${photo.external_url}`)
    : `${BACKEND_URL}/api/media/${photo.media_id}/file`;
  const isHero = !!photo.book_hero;
  const hasStory = !!(photo.book_caption || '').trim();

  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${isHero ? 'border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/8' : 'border-[hsl(var(--border))]'}`} data-testid={`photo-story-row-${photo.media_id}`}>
      <div className="flex items-center gap-3 p-2">
        <img src={thumbUrl} alt="" className="w-14 h-14 rounded-md object-cover shrink-0" style={{ objectPosition: focalPosition(photo) }} />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[hsl(var(--primary))] truncate">
            {photo.book_title || photo.caption || 'Untitled photo'}
          </div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
            {isHero && <span className="text-[hsl(var(--accent))] font-medium inline-flex items-center gap-1"><Star className="w-3 h-3 fill-current" />Hero</span>}
            {hasStory ? <span>Story added</span> : <span className="italic">No story yet</span>}
            {photo.book_date && <span>· {photo.book_date}</span>}
          </div>
        </div>
        <button
          onClick={onToggleExpand}
          className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] px-2"
          data-testid={`expand-photo-${photo.media_id}`}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-[hsl(var(--border))] p-3 space-y-3 bg-[hsl(var(--background))]/50">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isHero}
              onChange={(e) => onPatch({ book_hero: e.target.checked })}
              data-testid={`hero-toggle-${photo.media_id}`}
              className="mt-1"
            />
            <div>
              <div className="text-sm text-[hsl(var(--primary))] flex items-center gap-1">
                <Star className={`w-3.5 h-3.5 ${isHero ? 'fill-[hsl(var(--accent))] text-[hsl(var(--accent))]' : 'text-[hsl(var(--muted-foreground))]'}`} />
                Feature as Hero Photo
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                Hero photos render full-page with their own story block. Other photos get two-per-page.
              </div>
            </div>
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Title</span>
              <input
                defaultValue={photo.book_title || ''}
                onBlur={(e) => { if (e.target.value !== photo.book_title) onPatch({ book_title: e.target.value }); }}
                placeholder="Sunday Dinner"
                className="ml-input mt-1 w-full"
                data-testid={`book-title-${photo.media_id}`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Date (any precision)</span>
              <input
                defaultValue={photo.book_date || ''}
                onBlur={(e) => { if (e.target.value !== photo.book_date) onPatch({ book_date: e.target.value }); }}
                placeholder="1985 or July 1985"
                className="ml-input mt-1 w-full"
                data-testid={`book-date-${photo.media_id}`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Location</span>
              <input
                defaultValue={photo.book_location || ''}
                onBlur={(e) => { if (e.target.value !== photo.book_location) onPatch({ book_location: e.target.value }); }}
                placeholder="Savannah, Georgia"
                className="ml-input mt-1 w-full"
                data-testid={`book-location-${photo.media_id}`}
              />
            </label>
            {chaptersEnabled && (
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Chapter</span>
                <select
                  defaultValue={photo.book_chapter || ''}
                  onChange={(e) => onPatch({ book_chapter: e.target.value || null })}
                  className="ml-input mt-1 w-full"
                  data-testid={`book-chapter-${photo.media_id}`}
                >
                  <option value="">— Unassigned —</option>
                  {chapterOptions.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
            )}
          </div>

          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">The story behind this photo</span>
            <textarea
              defaultValue={photo.book_caption || ''}
              onBlur={(e) => { if (e.target.value !== photo.book_caption) onPatch({ book_caption: e.target.value }); }}
              rows={4}
              maxLength={2000}
              placeholder="What does this photo mean? Who's there, what happened, why does it matter?"
              className="ml-input mt-1 w-full"
              data-testid={`book-caption-${photo.media_id}`}
            />
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Story contributed by</span>
              <input
                defaultValue={photo.book_contributor_name || ''}
                onBlur={(e) => { if (e.target.value !== photo.book_contributor_name) onPatch({ book_contributor_name: e.target.value }); }}
                placeholder="Sarah Anderson"
                className="ml-input mt-1 w-full"
                data-testid={`book-contributor-${photo.media_id}`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Relationship</span>
              <input
                defaultValue={photo.book_contributor_relationship || ''}
                onBlur={(e) => { if (e.target.value !== photo.book_contributor_relationship) onPatch({ book_contributor_relationship: e.target.value }); }}
                placeholder="Daughter"
                className="ml-input mt-1 w-full"
                data-testid={`book-rel-${photo.media_id}`}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function AddMessageModal({ onClose, onSubmit }) {
  const [name, setName] = useState('');
  const [rel, setRel] = useState('');
  const [msg, setMsg] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="add-message-modal">
      <div className="bg-[hsl(var(--background))] rounded-lg shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Add a family message</h3>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
          A short tribute from a family member or friend. Appears in the "Messages" section of the book.
        </p>
        <div className="mt-4 space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name *" className="ml-input w-full" data-testid="message-name-input" />
          <input value={rel} onChange={(e) => setRel(e.target.value)} placeholder="Relationship (e.g. Wife, Son)" className="ml-input w-full" data-testid="message-rel-input" />
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Your message *" rows={4} maxLength={2000} className="ml-input w-full" data-testid="message-body-input" />
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="ml-btn-outline !py-1.5 !px-3 text-xs">Cancel</button>
          <button
            onClick={() => { if (name.trim() && msg.trim()) onSubmit(name.trim(), rel.trim(), msg.trim()); }}
            disabled={!name.trim() || !msg.trim()}
            className="ml-btn-gold !py-1.5 !px-3 text-xs"
            data-testid="submit-message-btn"
          >Add message</button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ onClose, data }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="memory-book-preview-modal">
      <div className="bg-[hsl(var(--background))] rounded-lg shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))] flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Book preview
          </h3>
          <button onClick={onClose} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" aria-label="Close">×</button>
        </div>
        {!data ? (
          <div className="py-12 flex items-center justify-center text-sm text-[hsl(var(--muted-foreground))]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Building preview…
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {data.page_count} pages · {(data.completion?.photo_count || 0)} photos · {(data.completion?.hero_count || 0)} hero
            </div>
            <ol className="text-sm space-y-1.5 list-decimal pl-5 marker:text-[hsl(var(--accent))]">
              <li><strong>Cover</strong> — {data.memorial?.full_name}</li>
              <li><strong>Dedication</strong></li>
              {(data.pages || []).map((p, i) => {
                if (p.kind === 'chapter_divider') return <li key={i} className="text-[hsl(var(--accent))]">Chapter {p.chapter_number}: {p.chapter_title}</li>;
                if (p.kind === 'hero') return <li key={i}><Sparkles className="w-3 h-3 inline-block mr-1 text-[hsl(var(--accent))]" />Hero: {p.title || '(untitled)'}</li>;
                if (p.kind === 'moment_pair') return <li key={i}>Moments: {(p.moments || []).map((m) => m.title || 'untitled').join(' · ')}</li>;
                return null;
              })}
              {(data.settings?.family_messages || []).length > 0 && <li><strong>Messages from family and friends</strong></li>}
              {data.settings?.show_stats_page && <li><strong>About this book</strong></li>}
              <li><strong>Closing page</strong> — QR + memorial URL</li>
            </ol>
            <p className="text-xs text-[hsl(var(--muted-foreground))] italic pt-3">
              This is just the table of contents. Close this and click <strong>Open Memory Book</strong>
              to read the actual interactive hardcover — a printable PDF copy is also available below it.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
